<template>
  <div>
    <div class="text-h5 q-mb-sm">
      Details
    </div>
    <div class="row q-col-gutter-sm items-start">
      <q-input
        v-model="registerType.name"
        class="col-4"
        label="Name"
        :rules="[val => !!val || 'Field is required']"
        bottom-slots
        :autofocus="!$q.platform.is.ios"
      />
      <q-select
        v-model="registerType.applicationType"
        class="col-4"
        label="Application Type"
        :options="Object.values(APPLICATION_TYPES)"
        :option-label="label => $_.startCase(label)"
        map-options
        emit-value
        bottom-slots
      />
      <q-select
        v-model="registerType.locationId"
        class="col-4"
        label="Location"
        :options="locations.data"
        option-label="name"
        option-value="id"
        map-options
        emit-value
        clearable
        bottom-slots
      />
      <q-select
        v-model="registerType.stripeTerminalLocationId"
        class="col-3"
        label="Stripe Terminal Location"
        :options="registerType.stripeTerminalLocationOptions"
        clearable
        bottom-slots
        map-options
        emit-value
      />
    </div>
    <div v-if="registerType.applicationType === APPLICATION_TYPES.KIOSK">
      <div
        class="row q-gutter-x-sm q-mb-sm"
      >
        <div class="col-3">
          <q-item-label>
            Display Zoom
          </q-item-label>
          <q-slider
            v-model="registerType.kioskOptions.view_port_zoom"
            label
            label-always
            switch-label-side
            type="number"
            class="q-mt-sm col-3"
            :min="0.5"
            :max="2"
            :step="0.05"
          />
          <q-tooltip>
            Increase to make everything appear larger or decrease to make smaller.
            This can be used to adjust scaling the application to match the size of your kiosk screen.
          </q-tooltip>
        </div>
        <q-select
          v-model="registerType.kioskOptions.homepage_advertising_display_type"
          class="col-3"
          label="Homepage Advertising Type"
          :rules="[val => !!val || 'Advertising Type is Required']"
          :options="Object.values(KIOSK_HOMEPAGE_ADVERTISING_DISPLAY_TYPES)"
          :option-label="label => $_.startCase(label)"
          map-options
          emit-value
          bottom-slots
        />
      </div>
      <div v-if="registerType.kioskOptions.homepage_advertising_display_type === KIOSK_HOMEPAGE_ADVERTISING_DISPLAY_TYPES.SIGNAGE">
        <div class="text-h5 q-mb-sm">
          Signage Options
        </div>
        <signage-builder
          class="q-my-md"
          exclude-screens
          include-media-button
          :display-url="registerType.kioskOptions.homepage_signage_url"
          :show-preview="false"
          @url-changed="(url) => registerType.kioskOptions.homepage_signage_url = url"
        />
      </div>
      <div v-else>
        <div class="text-h5 q-mb-sm">
          Carousel Option
        </div>
        <q-checkbox
          v-model="registerType.kioskOptions.homepage_carousel_include_trailer_autoplay"
          class="col-6 q-pb-md"
          label="Autoplay Movie Trailer"
        >
          <q-tooltip>
            Play the movie trailer in the carousel background
          </q-tooltip>
        </q-checkbox>
      </div>
      <div>
        <div class="text-h5 q-mb-sm">
          Ticket Options
        </div>
        <q-checkbox
          v-model="registerType.kioskOptions.disable_ticket_printing"
          class="col-6 q-pb-md"
          label="Disable Ticket Printing"
        >
          <q-tooltip>
            Disallow the printing of tickets. Order confirmations will be sent by email.
          </q-tooltip>
        </q-checkbox>
      </div>
      <div>
        <div class="text-h5 q-mb-sm">
          Food And Beverage Options
        </div>
        <q-checkbox
          v-model="registerType.allowKioskFoodAndBeverageOrdering"
          class="col-6 q-pb-md"
          label="Show Food And Beverage Button In Checkout"
        >
          <q-tooltip>
            Allow purchasing food and beverage in the checkout flow.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="registerType.kioskOptions.show_food_and_beverage_on_homepage"
          class="col-6 q-pb-md"
          label="Show Food And Beverage Button On Home Page"
        >
          <q-tooltip>
            Allow purchasing food and beverage before selecting any showing.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="registerType.kioskOptions.show_ticket_purchase_on_homepage"
          class="col-6 q-pb-md"
          label="Show Ticket Purchase Button On Home Page"
        >
          <q-tooltip>
            Allow purchasing tickets to a showing.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="registerType.kioskOptions.show_activities_purchase_on_homepage"
          class="col-6 q-pb-md"
          label="Show Activities Purchase Button On Home Page"
        >
          <q-tooltip>
            Allow purchasing tickets to an activity.
          </q-tooltip>
        </q-checkbox>
        <register-type-delivery-options
          :register-type="registerType"
        />
      </div>
      <div>
        <div class="text-h5 q-mb-sm">
          Homepage Preview
        </div>
        <div
          style="border: groove;"
        >
          <action-selection-page
            :preview="true"
            :register-type="registerType"
          />
        </div>
      </div>

      <div class="text-h5 q-my-md">
        Showtimes Display
      </div>
      <q-select
        v-model="registerType.kioskOptions.showings_display_type"
        class="col-3"
        label="Showtimes Display Type"
        :options="SECTION_TYPES.MOVIE_WIDGETS"
        :option-label="label => $_.startCase(label)"
        map-options
        emit-value
        bottom-slots
      />

      <explore-movies-editor
        v-if="registerType.kioskOptions.showings_display_type === SECTION_TYPES.EXPLORE_MOVIES"
        :section="showtimesSection"
        hide-preview
      />
      <showtimes-editor
        v-else
        :section="showtimesSection"
        hide-preview
      />
      <div class="text-h5 q-my-md">
        Style Options
      </div>
      <div class="row justify-around q-gutter-x-sm q-mb-sm">
        <div class="col-3">
          <q-item-label
            class="q-mt-sm text-bold"
          >
            Background Image
          </q-item-label>
          <image-uploader
            v-model="registerType.kioskOptions.background_image"
            :style="'opacity: ' + registerType.kioskOptions.background_image_opacity"
            :image-url="registerType.kioskBackgroundImageUrl"
            :model="registerType"
          />
          <div v-if="registerType.kioskOptions.background_image">
            <q-slider
              v-model="registerType.kioskOptions.background_image_opacity"
              label
              label-always
              switch-label-side
              type="number"
              class="q-mt-sm"
              :min="0"
              :max="1"
              :step="0.05"
            />
            <q-item-label
              class="text-center"
            >
              Transparency
            </q-item-label>
          </div>
        </div>
        <div class="col-3">
          <q-item-label
            class="q-mt-sm text-bold"
          >
            Welcome Screen Image
          </q-item-label>
          <image-uploader
            v-model="registerType.kioskOptions.welcome_screen_custom_image"
            :image-url="registerType.kioskWelcomeScreenCustomImageUrl"
            :model="registerType"
          />
        </div>
        <div class="col-3 column">
          <q-checkbox
            v-model="registerType.kioskOptions.welcome_screen_show_hero_text"
            label="Welcome Screen Show Hero Text"
            class="col-auto"
          />
          <q-checkbox
            v-model="registerType.kioskOptions.welcome_screen_show_logo"
            label="Welcome Screen Show Logo"
            class="col-auto"
          />
          <q-checkbox
            v-model="registerType.kioskOptions.welcome_screen_show_cta_button"
            label="Welcome Screen Show CTA Button"
            class="col-auto"
          />
        </div>
        <div class="col-12">
          <q-item-label
            class="q-mt-sm text-bold"
          >
            Custom Styles (CSS)
          </q-item-label>
          <css-editor
            v-model="registerType.styles"
          />
        </div>
      </div>
    </div>
    <div v-if="registerType.applicationType === APPLICATION_TYPES.REGISTER">
      <div
        v-if="loginDestinationOptions.length > 1"
        class="row q-col-gutter-sm items-start"
      >
        <q-toggle
          v-model="registerType.toggleLoginDestinationEnabled"
          stack
          class="col-3 q-mt-sm"
          label="Enable Destination Toggle"
          bottom-slots
        />
        <q-select
          v-model="registerType.defaultLoginDestination"
          class="col-3"
          label="Default Login Destination"
          :options="loginDestinationOptions"
          :option-label="(loginDestination) => LOGIN_DESTINATIONS_TYPE_MAP[loginDestination].label"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-select
          v-model="registerType.loginDestinations"
          class="col-4"
          label="Login Destinations"
          multiple
          :options="loginDestinationOptions"
          :option-label="(loginDestination) => LOGIN_DESTINATIONS_TYPE_MAP[loginDestination].label"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          map-options
          emit-value
          use-chips
        />
      </div>
      <div
        v-if="currentSite.allowCashPayments"
        class="text-h5 q-mb-md q-mt-lg"
      >
        Cash Counts
      </div>
      <div
        v-if="currentSite.allowCashPayments"
        class="row q-col-gutter-sm items-center"
      >
        <q-toggle
          v-model="registerType.allowCashPayments"
          class="col-4 q-pb-md"
          label="Allow Cash Payments"
          bottom-slots
        >
          <q-tooltip>
            Allow payments to be made with cash.
          </q-tooltip>
        </q-toggle>
        <div
          v-if="registerType.allowCashPayments"
          class="col-8 row q-col-gutter-sm items-center"
        >
          <q-checkbox
            v-model="registerType.requireCashSessionForTransactions"
            class="col-6 q-pb-md"
            label="Require Cash Sessions"
          >
            <q-tooltip>
              Require your staff to start a cash session before making any cash transactions.
            </q-tooltip>
          </q-checkbox>
          <model-form-select
            field="cashCountType"
            class="col-6"
            label="Cash Count Type"
            :options="$mh.asOptions(CASH_COUNT_TYPES.ALL)"
            map-options
            emit-value
            :rules="[val => (!!val && val !== '') || 'Field is required']"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  <div class="row items-center justify-between q-gutter-x-sm">
                    <span>{{ scope.opt.label }}</span>
                    <q-icon
                      name="info"
                      size="sm"
                    >
                      <q-tooltip>
                        {{ CASH_COUNT_TYPES_TOOLTIPS[scope.opt.value] }}
                      </q-tooltip>
                    </q-icon>
                  </div>
                </q-item-section>
              </q-item>
            </template>
            <q-tooltip>
              Count cash against cash trays or individual users for this register type.
            </q-tooltip>
          </model-form-select>
          <q-checkbox
            v-if="registerType.cashCountType === CASH_COUNT_TYPES.USER"
            v-model="registerType.requireUserCashSessionsForTabs"
            class="col-6 q-pb-md"
            label="Require User Cash Sessions for Tabs"
          />
          <q-checkbox
            v-model="registerType.cashCountRequiresAuthorization"
            label="Cash Count Requires Authorization"
            class="col-6 q-pb-md"
            bottom-slots
          >
            <q-tooltip>
              When opening the cash count dialog on the register, this setting controls if admin authorization is required.
            </q-tooltip>
          </q-checkbox>
        </div>
        <div
          v-if="registerType.allowCashPayments && registerType.cashCountType === CASH_COUNT_TYPES.CASH_TRAY"
          class="col-12 row q-col-gutter-sm items-center"
        >
          <q-checkbox
            v-model="registerType.enableRestrictedUserCashSessions"
            class="col-4 q-pb-md"
            label="Restrict Cash Sessions to Users"
          >
            <q-tooltip>
              Only allow the associated staff member or an admin to act on a given cash session or cash count. Other staff can still close open cash sessions.
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.enableAutomaticCashSessions"
            class="col-4 q-pb-md"
            label="Automated Cash Sessions"
          >
            <q-tooltip>
              Automatically close and open new cash sessions.
            </q-tooltip>
          </q-checkbox>
          <q-select
            v-model="registerType.automaticCashSessionsIntervalDays"
            class="col-4"
            label="Automated Cash Interval"
            :options="[
              { label: 'Daily', value: 1 },
              { label: 'Weekly', value: 7 }
            ]"
            map-options
            emit-value
            :disable="!registerType.enableAutomaticCashSessions"
            bottom-slots
            :rules="[val => !registerType.enableAutomaticCashSessions || !!val || 'Field is required']"
          />
        </div>
      </div>
      <div class="text-h5 q-mb-md q-mt-lg">
        Customer Facing Display
      </div>
      <div class="row q-col-gutter-sm items-center">
        <q-select
          v-model="registerType.customerDisplayIdleType"
          class="col-6"
          label="Customer Display Idle Type"
          :options="$_.map(CUSTOMER_DISPLAY_IDLE_TYPES.ALL, idleType => {
            return { label: $_.startCase(idleType.replaceAll('-', ' ')), value: idleType }
          })"
          :rules="[val => val !== null || 'Field is required']"
          bottom-slots
          map-options
          emit-value
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ CUSTOMER_DISPLAY_IDLE_TYPES_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Configure what your customer facing displays present when an order is not in progress.
          </q-tooltip>
        </q-select>
        <q-select
          v-if="$_.includes(CUSTOMER_DISPLAY_IDLE_TYPES.MEDIAS, registerType.customerDisplayIdleType)"
          v-model="registerType.customerDisplayMediaClass"
          class="col-6"
          :label="`Customer Display Media Class${registerType.customerDisplayIdleType !== CUSTOMER_DISPLAY_IDLE_TYPES.MEDIA ? ' (Optional)' : ''}`"
          :options="signageMediaClasses.data"
          option-label="name"
          map-options
          bottom-slots
          :rules="[val => registerType.customerDisplayIdleType !== CUSTOMER_DISPLAY_IDLE_TYPES.MEDIA || val !== null || 'Field is required']"
        />
        <div
          v-else
          class="col-6"
        />
      </div>

      <div class="text-h5 q-mb-md q-mt-lg">
        Delivery Available
      </div>
      <register-type-delivery-options
        :register-type="registerType"
      />
      <div class="text-h5 q-mb-md q-mt-lg">
        Printing
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-select
          v-model="registerType.printTemplates"
          map-options
          emit-value
          multiple
          use-chips
          class="col-12"
          label="Custom Print Templates"
          :options="$_.map(publishedPrintTemplates.data, printTemplate => { return { value: printTemplate.id, label: printTemplate.name } })"
          bottom-slots
          @keyup.enter.stop
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-checkbox
          v-model="registerType.requireAuthorizationForPrintingItems"
          data-class="col-3"
          label="Require Admin Authorization When Printing Tickets"
          bottom-slots
        >
          <q-tooltip>
            On POS, order tickets are automatically printed upon purchase so employees don't need to manually print them.
            <br>
            If an employee tries to reprint an order, this option determines whether they need admin approval to do so.
            <br>
            This does not affect reprinting order receipts — employees can still reprint receipts.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="registerType.groupTicketPrintingEnabled"
          data-class="col-3"
          label="Enable Group Ticket Printing"
          bottom-slots
        >
          <q-tooltip>
            When multiple tickets are purchased for the same showing, a single ticket can be printed for each showing in the order.
          </q-tooltip>
        </q-checkbox>
      </div>
      <div class="row q-mt-sm q-col-gutter-sm items-start">
        <q-toggle
          v-model="registerType.printTemplateConfiguration.overrideTicketTemplate"
          label="Override Ticket Template"
          class="col-3"
        />
        <q-select
          v-if="registerType.printTemplateConfiguration.overrideTicketTemplate"
          v-model="registerType.printTemplateConfiguration.ticketTemplateId"
          map-options
          :options="$_.filter(bocaPrintTemplates, template => template.templateType === BOCA_TEMPLATE_TYPES.TICKET)"
          option-label="name"
          option-value="id"
          label="Boca Ticket Template"
          emit-value
          class="col-3"
        />
      </div>
      <div class="row q-mt-sm q-col-gutter-sm items-start">
        <q-toggle
          v-model="registerType.printTemplateConfiguration.overrideGiftCardTemplate"
          label="Override Gift Card Template"
          class="col-3"
        />
        <q-select
          v-if="registerType.printTemplateConfiguration.overrideGiftCardTemplate"
          v-model="registerType.printTemplateConfiguration.giftCardTemplateId"
          map-options
          :options="$_.filter(bocaPrintTemplates, template => template.templateType === BOCA_TEMPLATE_TYPES.GIFT_CARD)"
          option-label="name"
          option-value="id"
          label="Boca Gift Card Template"
          emit-value
          class="col-3"
        />
      </div>
      <div class="row q-mt-sm q-col-gutter-sm items-start">
        <q-toggle
          v-model="registerType.printTemplateConfiguration.overrideVoucherTemplate"
          label="Override Voucher Template"
          class="col-3"
        />
        <q-select
          v-if="registerType.printTemplateConfiguration.overrideVoucherTemplate"
          v-model="registerType.printTemplateConfiguration.voucherTemplateId"
          map-options
          :options="$_.filter(bocaPrintTemplates, template => template.templateType === BOCA_TEMPLATE_TYPES.VOUCHER)"
          option-label="name"
          option-value="id"
          label="Boca Voucher Template"
          emit-value
          class="col-3"
        />
      </div>

      <div class="row q-mt-sm q-col-gutter-sm items-start">
        <q-toggle
          v-model="registerType.printTemplateConfiguration.useThermalReceipt"
          label="Print Basic Receipts"
          class="col-3"
        >
          <q-tooltip>
            Print a basic thermal receipt for orders.
            This will increase the speed of printing and is more of a traditional receipt style.
          </q-tooltip>
        </q-toggle>
        <q-checkbox
          v-if="registerType.printTemplateConfiguration.useThermalReceipt"
          v-model="registerType.printTemplateConfiguration.thermalReceiptPrintLogo"
          label="Print Logo on Basic Receipt"
          class="col-3"
        >
          <q-tooltip>
            Your site's logo will be printed on the thermal receipt.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-if="currentUser.superUser"
          v-model="registerType.autoPrintReceipts"
          label="Auto Print Receipts (Beta)"
          class="col-3"
        >
          <q-tooltip>
            Receipt printing is toggled on by default when electron version >= 1.1.7
          </q-tooltip>
        </q-checkbox>
      </div>

      <div class="text-h5 q-mb-md q-mt-lg">
        More Options
      </div>
      <div class="column q-col-gutter-sm items-start">
        <div class="full-width row q-col-gutter-sm items-start q-mb-sm">
          <q-checkbox
            v-model="registerType.keepShowingSelection"
            class="col-3"
            label="Keep Showing Selection"
            bottom-slots
          >
            <q-tooltip>
              After completing an order keep the showing selection to quickly complete an order for this same showing
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.showFavoritesSection"
            class="col-3"
            label="Show Favorites Section"
            bottom-slots
          >
            <q-tooltip>
              Show a list of buttons for adding the user's favorite items
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-if="registerType.showFavoritesSection"
            v-model="registerType.autoAddUsualOrder"
            class="col-3"
            label="Automatically Add Usual Order"
            bottom-slots
          >
            <q-tooltip>
              If a user has a Usual order (a group of things they order very often) automatically add it to the cart when user is selected
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.enableConfirmSelectedUser"
            label="Show Confirm User Identity Dialog"
            class="col-3"
            bottom-slots
          >
            <q-tooltip>
              Require the employee to confirm the selected user identity attaching the user to the order
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.searchOptions.employee_user_search_enabled"
            class="col-3"
            label="Show Employees in Search"
            bottom-slots
          >
            <q-tooltip>
              Allow employees to appear in search. Employees can still be served when disabled by presenting their member card.
            </q-tooltip>
          </q-checkbox>
        </div>
        <div class="full-width row q-col-gutter-sm items-start q-mb-sm">
          <q-checkbox
            v-model="registerType.allowTabOrders"
            class="col-3"
            label="Allow Tab Orders"
            bottom-slots
          >
            <q-tooltip>
              Allow employees to open Tab Orders (place order without paying first)
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.allowInvoiceOrders"
            class="col-3"
            label="Allow Invoice Orders"
            bottom-slots
          >
            <q-tooltip>
              Allow employees to open Invoice Orders
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.searchOptions.invoice_user_search_enabled"
            class="col-3"
            label="Show Invoice Users in Search"
            bottom-slots
          >
            <q-tooltip>
              Allow invoiceable users to appear in search. Invoiceable users can still be served when disabled by presenting their member card.
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.searchOptions.invoice_order_search_enabled"
            class="col-3"
            label="Show Invoice Orders in Search"
            bottom-slots
          >
            <q-tooltip>
              Allow invoiced orders to appear in search. Invoiced orders can still be served when disabled by presenting their order receipt.
            </q-tooltip>
          </q-checkbox>
        </div>
        <div class="full-width row q-col-gutter-sm items-start">
          <q-checkbox
            v-model="registerType.showTipping"
            class="col-3"
            label="Show Tipping"
            bottom-slots
          >
            <q-tooltip>
              Show a button the employee can press to present a tipping screen to the customer
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.stripeOnReaderTips"
            class="col-3"
            label="Show Tip Prompt on Card Reader"
            bottom-slots
          >
            <q-tooltip>
              Can take up to 5 minutes to take effect
              <ul>
                <li>This removes the tip dialog from the register.</li>
                <li>The dialog will now be displayed to the customer on the credit card reader.</li>
              </ul>
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.showAddSavedCardButton"
            class="col-3"
            label="Show Add Saved Card Button"
            bottom-slots
          >
            <q-tooltip>
              Show a button the employee can press to present a screen to the customer to add a saved card
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.usePreauthForOrderSavedCards"
            class="col-3"
            label="Use Pre-Auth for Order Saved Cards"
            bottom-slots
          >
            <q-tooltip>
              When Pre-auth is enabled, the saved card will be pre-authorized for the order amount.
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-if="currentCircuit.enableSms"
            v-model="registerType.enableSmsReceipts"
            class="col-3"
            label="Enable SMS Receipts"
            bottom-slots
          >
            <q-tooltip>
              Enable SMS receipts for orders
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-model="registerType.enableEmailReceipts"
            class="col-3"
            label="Enable Email Receipts"
            bottom-slots
          >
            <q-tooltip>
              Enable email receipts for orders
            </q-tooltip>
          </q-checkbox>
          <q-checkbox
            v-if="currentSite.allowCashPayments && registerType.allowCashPayments"
            v-model="registerType.showQuickCash"
            class="col-3"
            label="Show Quick Cash"
            bottom-slots
          >
            <q-tooltip>
              Add a quick cash button the the cart section for exact change cash orders
            </q-tooltip>
          </q-checkbox>
          <q-select
            v-if="currentSite.allowCashPayments && registerType.allowCashPayments && registerType.showQuickCash"
            v-model="registerType.quickCashAmountOptions"
            class="col-4"
            new-value-mode="add-unique"
            label="Quick Cash Amounts"
            multiple
            hide-dropdown-icon
            use-input
            map-options
            emit-value
            use-chips
            bottom-slots
            :error="addNewOptionError !== null"
            :error-message="addNewOptionError"
            :option-label="option => $i18n.n(option, 'currency')"
            @update:model-value="value => validateOption(value)"
            @new-value="addNewOption"
          >
            <q-tooltip>Up to 4 options is recommended, maximum 10</q-tooltip>
          </q-select>
          <q-btn
            v-if="currentSite.allowCashPayments && registerType.allowCashPayments && registerType.showQuickCash"
            class="col-2 q-mt-md"
            label="Set Display Order"
            @click="$refs.quickCashAmountOptionsOrderDialog.show()"
          />
        </div>
        <div class="full-width row q-col-gutter-sm items-start q-mb-sm">
          <q-checkbox
            v-model="registerType.autoLogout"
            class="col-3 q-mt-sm"
            label="Auto Logout"
            bottom-slots
          >
            <q-tooltip>
              Automatically log out employees after a period of idle time
            </q-tooltip>
          </q-checkbox>
          <q-input
            v-model.number="registerType.autoLogoutMinutes"
            :disable="!registerType.autoLogout"
            class="col-3"
            label="Idle Minutes"
            type="number"
            min="0"
            max="280"
            bottom-slots
          />
        </div>
        <div class="full-width row q-col-gutter-sm items-start">
          <div class="col-3">
            <q-item-label class="q-mb-md">
              Display Zoom
            </q-item-label>
            <q-slider
              v-model="registerType.registerOptions.view_port_zoom"
              label
              label-always
              switch-label-side
              type="number"
              class="q-mt-sm col-3"
              :min="0.5"
              :max="2"
              :step="0.05"
            />
            <q-tooltip>
              Increase to make everything appear larger or decrease to make smaller.
              This can be used to adjust scaling the application to match the size of your Register screen.
            </q-tooltip>
          </div>
        </div>
      </div>
      <div class="text-h5 q-my-md">
        Mobile
      </div>
      <div class="row q-my-md">
        <q-checkbox
          v-model="registerType.promptForDeliveryOnMobile"
          class="col-3 q-mt-sm"
          label="Prompt For Delivery On Mobile POS"
          bottom-slots
        >
          <q-tooltip>
            When an item is added to the cart, prompt the user to enter delivery information.
          </q-tooltip>
        </q-checkbox>

        <q-select
          v-model="registerType.mobileHomepageViewType"
          class="col-4"
          :option-label="(label) => $_.startCase(label)"
          label="Mobile Homepage View"
          map-options
          :options="MOBILE_HOME_VIEW_TYPES.ALL"
        />
      </div>
      <div class="text-h5 q-my-md">
        Style Options
      </div>
      <div class="row justify-around q-gutter-x-sm q-mb-sm">
        <div class="col-12">
          <q-item-label
            class="q-mt-sm text-bold"
          >
            Custom Styles (CSS)
          </q-item-label>
          <css-editor
            v-model="registerType.styles"
          />
        </div>
      </div>
    </div>
    <set-display-order-dialog
      ref="quickCashAmountOptionsOrderDialog"
      :list="registerType.quickCashAmountOptions"
      @reorder="handleReorder"
    />
  </div>
</template>

<script>
import { INTERVALS as CASH_COUNT_INTERVALS, TYPES as CASH_COUNT_TYPES } from 'src/models/cash/CashCount'
import Section, { SECTION_TYPES } from 'src/models/page/Section'
import { BOCA_TEMPLATE_TYPES, PRINTER_TYPE_OPTIONS } from 'src/models/print-template/PrintTemplate'
import RegisterType, { APPLICATION_TYPES, CUSTOMER_DISPLAY_IDLE_TYPES, KIOSK_HOMEPAGE_ADVERTISING_DISPLAY_TYPES, LOGIN_DESTINATIONS, LOGIN_DESTINATIONS_TYPE_MAP, MOBILE_HOME_VIEW_TYPES } from 'src/models/register/RegisterType'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import RegisterTypeDeliveryOptions from 'src/modules/admin/register-type/components/RegisterTypeDeliveryOptions'
import ExploreMoviesEditor from 'src/modules/admin/section/ExploreMoviesEditor'
import ShowtimesEditor from 'src/modules/admin/section/ShowtimesEditor'
import SignageBuilder from 'src/modules/admin/signage/SignageBuilder'
import ActionSelectionPage from 'src/modules/kiosk/components/ActionSelectionPage'
import { defineAsyncComponent } from 'vue'

const CASH_COUNT_TYPES_TOOLTIPS = {
  [CASH_COUNT_TYPES.CASH_TRAY]: 'Count cash against cash trays. All cash counts must have a cash tray. Cash payments should be deposited into the cash tray.',
  [CASH_COUNT_TYPES.USER]: 'Count cash against individual users. Cash payments will follow users across stations without cash trays. Cash payments are held by the user.'
}

const CUSTOMER_DISPLAY_IDLE_TYPES_TOOLTIPS = {
  'splash-image': 'Present your cinema logo in fullscreen.',
  media: 'Present media items from a selected media class.',
  signage: 'Present signage enabled movie content and media items from a selected media class.'
}

export default {
  name: 'RegisterTypeDetail',
  components: {
    SetDisplayOrderDialog,
    SignageBuilder,
    ActionSelectionPage,
    ShowtimesEditor,
    ImageUploader,
    RegisterTypeDeliveryOptions,
    CssEditor: defineAsyncComponent(() => import('src/modules/consumer/components/CssEditor.vue')),
    ExploreMoviesEditor
  },
  configuration: ['signageMediaClasses', 'currentSite', 'currentCircuit', 'locations', 'deliveryTypes', 'publishedPrintTemplates'],
  context: ['currentUser'],
  props: {
    registerType: {
      type: RegisterType,
      required: true
    }
  },
  data () {
    return {
      showDeliveryPriorityForm: false,
      addNewOptionError: null,
      showtimesSection: new Section()
    }
  },
  computed: {
    loginDestinationOptions () {
      const loginDestinationOptions = [LOGIN_DESTINATIONS.REGISTER.value]

      if (this.currentSite.timeClockConfig.time_clock_enabled) {
        loginDestinationOptions.push(LOGIN_DESTINATIONS.TIME_CARD.value)
      }

      return loginDestinationOptions
    },
    sectionConfigValues () {
      return Object.values(this.showtimesSection.config)
    },
    bocaPrintTemplates () {
      return _.filter(this.publishedPrintTemplates.data, template => template.printerType === PRINTER_TYPE_OPTIONS.BOCA)
    }
  },
  watch: {
    'sectionConfigValues' () {
      this.registerType.kioskOptions.showtimes_config = this.showtimesSection.config
    },
    'registerType.id' () {
      this.setupShowtimeConfig()
    },
    'registerType.applicationType' () {
      this.setupShowtimeConfig()
    },
    'registerType.kioskOptions' () {
      if (this.registerType.kioskOptions.showings_display_type !== SECTION_TYPES.EXPLORE_MOVIES) this.registerType.kioskOptions.showings_display_type = SECTION_TYPES.SHOWTIMES
    }
  },
  mounted () {
    this.setupShowtimeConfig()
  },
  created () {
    _.extend(this, {
      APPLICATION_TYPES,
      BOCA_TEMPLATE_TYPES,
      CASH_COUNT_INTERVALS,
      CASH_COUNT_TYPES,
      CASH_COUNT_TYPES_TOOLTIPS,
      CUSTOMER_DISPLAY_IDLE_TYPES,
      CUSTOMER_DISPLAY_IDLE_TYPES_TOOLTIPS,
      KIOSK_HOMEPAGE_ADVERTISING_DISPLAY_TYPES,
      LOGIN_DESTINATIONS_TYPE_MAP,
      PRINTER_TYPE_OPTIONS,
      SECTION_TYPES,
      MOBILE_HOME_VIEW_TYPES
    })
  },
  methods: {
    addNewOption (option) {
      if (option !== '' && this.validationMessage(option) === null) {
        this.registerType.quickCashAmountOptions.push(Number.parseFloat(option))
      }
    },
    setupShowtimeConfig () {
      if (this.registerType?.applicationType !== APPLICATION_TYPES.KIOSK) return
      if (!this.registerType.kioskOptions?.showtimes_config) {
        this.registerType.kioskOptions.showtimes_config = this.showtimesSection.config
      } else {
        _.each(Object.keys(this.registerType.kioskOptions.showtimes_config), (key) => {
          this.showtimesSection.config[key] = this.registerType.kioskOptions.showtimes_config[key]
        })
      }
    },
    validateOption (option) {
      this.addNewOptionError = this.validationMessage(option)
    },
    validationMessage (value) {
      if (value === '') return null
      if (this.registerType.quickCashAmountOptions.length > 9) {
        return 'Unable to add, maximum number of amounts is 10'
      }
      const quickCashAmount = Number.parseFloat(value)
      if (isNaN(quickCashAmount)) {
        return 'Value must be a number'
      }
      if (this.registerType.quickCashAmountOptions.includes(quickCashAmount)) {
        return 'Amount already added'
      }
      if (quickCashAmount < 0.1 || quickCashAmount > 1000) {
        return 'Amount must be between 0.1 - 1000'
      }
      return null
    },
    handleReorder ({ reorderedList }) {
      this.registerType.quickCashAmountOptions = reorderedList
    }
  }
}
</script>
